import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from 'react-admin';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Container,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  ChevronRight,
  Build as BuildIcon,
  Category as CategoryIcon,
  ViewKanban as BoardIcon,
  Palette as BrandingIcon,
  Flag as FlagIcon,
  Place as ZoneIcon,
  CalendarMonth as CalendarIcon,
  Dashboard as ServiceLineIcon,
  AccountTree as WorkflowIcon,
  Public as PortalIcon,
  Group as UsersIcon,
  InventoryOutlined,
  Description as FormTemplateIcon,
} from '@mui/icons-material';
import { useAuthState } from 'react-admin';
import { PageTitle } from '../components/PageTitle';

interface ConfigurationItem {
  id: string;
  title: string;
  description: string;
  icon: React.ReactElement;
  path: string;
  requiredPermissions?: string[];
}

const configurationItems: ConfigurationItem[] = [
  {
    id: 'calendar-settings',
    title: 'Calendar Settings',
    description: 'Configure business hours and calendar display settings',
    icon: <CalendarIcon />,
    path: '/configuration/calendar-settings',
  },
  {
    id: 'form-templates',
    title: 'Form Templates',
    description: 'Create and edit form templates used for data collection in the mobile app',
    icon: <FormTemplateIcon />,
    path: '/configuration/forms',
  },
  {
    id: 'workflows',
    title: 'Task Workflows',
    description: 'Manage operational workflows and their stages for task management',
    icon: <WorkflowIcon />,
    path: '/workflows',
  },
  {
    id: 'portal-settings',
    title: 'Customer Portal',
    description: 'Configure customer portal settings and default workflows',
    icon: <PortalIcon />,
    path: '/configuration/portal-settings',
  },
  {
    id: 'branding',
    title: 'Branding',
    description: 'Customize your brand colors, logo, and visual appearance',
    icon: <BrandingIcon />,
    path: '/configuration/branding',
  },
  {
    id: 'feature-flags',
    title: 'Feature Flags',
    description: 'Control which features and menu items are visible to users',
    icon: <FlagIcon />,
    path: '/configuration/feature-flags',
  },
  {
    id: 'boards',
    title: 'Opportunity Boards',
    description: 'Manage boards and stages for organizing opportunities',
    icon: <BoardIcon />,
    path: '/boards',
  },
  {
    id: 'service-blueprints',
    title: 'Service Blueprints',
    description: 'Manage service templates and blueprints for quick service creation',
    icon: <BuildIcon />,
    path: '/crm/service-blueprints',
  },
  {
    id: 'customer-types',
    title: 'Customer Types',
    description: 'Define customer categories and their properties',
    icon: <CategoryIcon />,
    path: '/crm/customer-types',
  },
  {
    id: 'zones',
    title: 'Zones',
    description: 'Manage geographic zones and territories for organizing customers',
    icon: <ZoneIcon />,
    path: '/crm/zones',
  },
  {
    id: 'material-categories',
    title: 'Material Categories and Materials',
    description: 'Manage material categories and individual materials for task actuals tracking',
    icon: <InventoryOutlined />,
    path: '/configuration/material-categories',
  },
  {
    id: 'timetracking-activities',
    title: 'Time Activities',
    description: 'Define activity types for time tracking and timer segments',
    icon: <CalendarIcon />,
    path: '/timetracking/activities',
  },
];

export const ConfigurationList = () => {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const { identity, isLoading } = useAuthState();

  // Also check localStorage as fallback
  const storedUser = React.useMemo(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const isEnterpriseAdmin =
    (identity as any)?.isEnterpriseAdmin === true ||
    storedUser?.isEnterpriseAdmin === true;

  React.useEffect(() => {
    console.log('ConfigurationList - isEnterpriseAdmin:', isEnterpriseAdmin, 'identity:', identity, 'isLoading:', isLoading);
  }, [isEnterpriseAdmin, identity, isLoading]);

  // Redirect enterprise admins to their separate configuration
  React.useEffect(() => {
    if (isEnterpriseAdmin && !isLoading) {
      console.log('Redirecting to enterprise admin configuration');
      navigate('/configuration/enterprise-admin');
    }
  }, [isEnterpriseAdmin, isLoading, navigate]);

  // Don't render tenant configuration if user is enterprise admin
  if (isEnterpriseAdmin) {
    return null;
  }

  // Filter configuration items based on user permissions
  const visibleItems = configurationItems.filter(item => {
    if (!item.requiredPermissions) return true;
    return item.requiredPermissions.some(permission => permissions?.includes(permission));
  });

  return (
    <Box>
      <PageTitle
        title="Configuration"
        subtitle="Manage system settings and configurations"
        icon={<SettingsIcon />}
      />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>

        <List sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
          {visibleItems.map((item, index) => (
            <React.Fragment key={item.id}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  sx={{
                    py: 3,
                    px: 3,
                    '&:hover': {
                      backgroundColor: 'rgba(28, 81, 149, 0.04)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon sx={{ color: '#1C5195' }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="h6" component="div">
                        {item.title}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {item.description}
                      </Typography>
                    }
                  />
                  <ChevronRight sx={{ color: 'text.secondary' }} />
                </ListItemButton>
              </ListItem>
              {index < visibleItems.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Container>
    </Box>
  );
};
